<template>
  <div class="ac-detail-container">
    <h1 class="page-title">autoconnect: brand follow up</h1>

    <ErrorBanner />

    <loading-icon v-if="isLoading"/>

    <div v-else-if="!isLoading && !!autoCampaign" class="details-container">
      <div class="settings section">
        <h2 class="data-title center-align">{{ autoCampaign.merchant_name }}</h2>
        <h2 class="status-container data-title center-align">
          status:
          <span :class="status">
            {{ status }}
          </span>
        </h2>

        <div class="settings-label">
          <label class="data-title">settings:</label>
        </div>

        <sb-table
          class="settings-table left-right-align"
          :columns="columns"
          :rows="rows"
        ></sb-table>
      </div>

      <contents
        class="section"
        :messageDetails="messageDetails"
        :hasNotificationImgMeta="hasNotificationImgMeta"
      />

      <stats
        class="section"
        :extension="extension"
        :hasAttachedMedia="hasAttachedMedia"
        :hasNotificationImgMeta="hasNotificationImgMeta"
        :notificationImage="messageDetails.template_image"
        :stats="stats"
      />

      <extension
        v-if="extension && extension.name !== '--'"
        class="section"
        :extension="extension"
        :isOffer="isOffer"
      />
    </div>
  </div>
</template>

<script>
import Contents from './Contents.vue'
import Extension from './Extension.vue'
import LoadingIcon from '../Shared/LoadingIcon.vue'
import Stats from './Stats.vue'
import { autoCampaignAPI } from '../../services/api/autoCampaignAPI'
import { ErrorBanner, UnexpectedError } from '@springbig/sb-components'
import { format } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'

export default {
  props: {
    currentAdvertiser: Object
  },

  name: 'AutoCampaignDetail',

  components: {
    Contents,
    ErrorBanner,
    Extension,
    LoadingIcon,
    Stats
  },

  data() {
    return {
      columns: [
        { label: '', key: 'dataKey' },
        { label: '', key: 'dataValue' }
      ],
      autoCampaign: null,
      autoCampaignAttachments: null,
      autoCampaignId: null,
      extensionOwnerType: 'AutoCampaignMessage',
      isLoading: true,
      mms: null,
      reward: null,
      rewardType: null,
      rows: [],
      stats: null
    }
  },

  created() {
    this.autoCampaignId = this.$route?.params?.id

    if (this.autoCampaignId) {
      this.getAutoCampaignData()
    } else {
      this.$store.dispatch('ADD_ERROR', new UnexpectedError('500'))
    }
  },

  computed: {
    endDate() {
      const endDateStr = this.autoCampaign?.argument_2
      return endDateStr ? format(new Date(endDateStr), 'MM/dd/yy') : 'incomplete'
    },

    extension() {
      const extension = { name: '--' }
      switch (this.messageDetails.extension_type) {
        // digital voucher
        case 1:
          extension.name = 'digital voucher'
          extension.value = this.reward
          extension.needOffer = true
          break;

        // wallet target
        case 3:
          extension.name = 'wallet target'
          extension.value = this.messageDetails.wallet_target
          break;

        // any link
        case 4:
          extension.name = 'any link'
          extension.value = this.messageDetails.any_links
          break;

        // bonus points
        case 5:
          extension.name = 'bonus points'
          extension.value = this.reward
          extension.needOffer = true
          break;

        // instant points
        case 6:
          extension.name = 'instant points'
          extension.value = this.reward
          break;
      }
      return extension
    },

    hasAttachedMedia() {
      return this.mms?.length > 0
    },

    hasNotificationImgMeta() {
      const metas = this.autoCampaign.metas
      if (!metas) return false
      else {
        const notificationImgMeta = metas.find(meta => meta.title === 'notification_mms' && meta.message === '1')
        return !!notificationImgMeta
      }
    },

    isOffer() {
      return this.messageDetails && [1, 5, 6].includes(this.messageDetails.extension_type)
    },

    message() {
      return this.scope?.messages[0]
    },

    messageDetails() {
      return {
        any_links: this.message?.any_links,
        extension_type: this.message?._type,
        id: this.message?.id,
        image_type: this.message?.image_type,
        mms: this.mms?.map((mms) => mms.url),
        mock_url: this.message?.mock_url,
        reward_id: this.message?.reward_id,
        send_at: this.message?.send_at,
        sms_content: this.message?.sms_content,
        template_content: this.message?.template_content,
        template_image: this.scope?.template_image,
        wallet_target: this.message?._type_options
      }
    },

    scope() {
      return this.autoCampaign.scopes[0]
    },

    startDate() {
      const startDateStr = this.autoCampaign?.argument_1
      return startDateStr ? format(new Date(startDateStr), 'MM/dd/yy') : 'incomplete'
    },

    status() {
      return this.autoCampaign.enabled ? 'enabled' : 'disabled'
    },

    timeToSend() {
      const timeToSend = this.autoCampaign?.send_at_local
      if (timeToSend) {
        const timeToSendDate = new Date(timeToSend)
        const timeToSendUtcDate = new Date(timeToSendDate.getTime() + timeToSendDate.getTimezoneOffset() * 60000)
        return format(timeToSendUtcDate, 'h:mmaaa')
      } else {
        return 'incomplete'
      }
    }
  },

  methods: {
    async getAutoCampaignData() {
      try {
        this.isLoading = true
        this.$store.dispatch('DISMISS_ERRORS', { type: UnexpectedError })

        const data = await autoCampaignAPI.getAutoCampaign(this.currentAdvertiser.id ,this.autoCampaignId)

        if (data?.auto_campaign) {
          this.autoCampaign = data.auto_campaign
          this.autoCampaignAttachments = data.auto_campaign?.attachments
          this.reward = data.auto_campaign.reward
          this.stats = data.auto_campaign.stats

          this.configureAutoCampaignDisplayData()
          this.configureAttachmentsData()
        } else {
          this.$store.dispatch('ADD_ERROR',new UnexpectedError(new Error(401)))
        }
      } catch (error) {
        this.$store.dispatch('ADD_ERROR', new UnexpectedError(error))
      }
      this.isLoading = false
    },

    configureAutoCampaignDisplayData() {
      this.rows = [
        { dataKey: 'merchant', dataValue: this.autoCampaign?.merchant_name },
        { dataKey: 'start date', dataValue: this.startDate },
        { dataKey: 'end date', dataValue: this.endDate },
        { dataKey: 'frequency', dataValue: this.autoCampaign.argument_3 || 'incomplete' },
        { dataKey: 'send day of the week', dataValue: this.autoCampaign.argument_4 },
        { dataKey: 'time to send', dataValue: this.timeToSend },
        {
          dataKey: 'created at',
          dataValue: `${this.formatDateString(this.autoCampaign.created_at
          )} at ${this.formatDateString(this.autoCampaign.created_at, 'h:mmaaa')}`,
        },
        {
          dataKey: 'updated at',
          dataValue: `${this.formatDateString(this.autoCampaign.updated_at
          )} at ${this.formatDateString(this.autoCampaign.updated_at, 'h:mmaaa')}`,
        },
      ]
    },

    configureAttachmentsData() {
      const hasAttachments = this.autoCampaignAttachments?.length > 0
      if (hasAttachments) {
        this.mms = this.autoCampaignAttachments.filter(
          (attachment) => attachment.label === 'mms'
        )
      }
    },

    formatDateString(dateString, formatString = 'MM/dd/yy') {
      if (!dateString) return '--'

      const parsedDate = utcToZonedTime(dateString, this.autoCampaign.merchant_time_zone)
      return format(parsedDate, formatString)
    },
  },
}
</script>

<style lang="sass">
@import '../../assets/sass/colors'

.ac-detail-container
  text-align: center
  padding-top: 2rem

  .page-title
    margin-bottom: 2rem

  .data-title
    color: $title-color
    text-align: left
    margin-bottom: .5em

  .sb-table
    .grid-content
      &:nth-child(even)
        background-color: $active-primary-background

  .left-right-align
    .grid-content
      padding: .25em .5em
      div:first-child
        text-align: left
      div:last-child
        text-align: right
        word-break: break-all

  .details-container
    margin: 0 auto
    width: 70%

    .center-align
      text-align: center
      margin-bottom: .25em

    .status-container
      .enabled
        color: $active-status
      .disabled
        color: $negative-hover

    .settings-label
      text-align: left
      margin-bottom: .4em

    .section
      margin-bottom: 2em
</style>
